import { toJS } from "mobx";
import {
  ILog,
  buildIglooPayload,
  getDeviceHost,
  getDeviceData,
  getPageURL,
  type UploadEvent,
  type ContestExitType,
} from "@ihr-radioedit/inferno-core";
import { IglooManage } from "../abstracts/manager/igloo-manager.abstract";
import { AnalyticsEvent, CcpaStatus, ContestEvent, ShareEvent } from "../abstracts/store/base-store.abstract";
import { isIHRSession } from "../auth/backend";
import { getDeviceId } from "../services/variant-test";
import type { Store } from "../stores";
import { isWindowDefined } from "../utilities/window";

const log = ILog.logger("igloo.ts");

declare global {
  interface Window {
    iHeartAnalytics: {
      setGlobalData: (data: any) => void;
      track: (event: any) => void;
    };
  }
}

/** Igloo Analytics Manager */
export class IglooManager extends IglooManage {
  protected _library?: Promise<void>;
  protected sdkLoaded = false;
  protected analyticsSnapshot = "";
  protected ccpaStatus?: string;

  constructor(protected store: Store) {
    super();
    const { session } = store;

    store.onShareAction.subscribe((event: ShareEvent) => {
      this.trackShareAction(event);
    });

    store.onAnalyticsAction.subscribe((event: AnalyticsEvent) => {
      if (event.context === "load_more") {
        this.trackPageView();
      } else {
        this.trackClickAction(event);
      }
    });

    store.onContestAction.subscribe((event: ContestEvent) => {
      this.trackContestAction(event);
    });

    if (session.currentSession) {
      this.ccpaStatus = store.getCcpaStatus();
      session.currentSession.onStatusChanged.sub(() => {
        this.ccpaStatus = store.getCcpaStatus();
      });
    }

    store.igloo = this;
  }

  private get isReady(): boolean {
    return isWindowDefined() && !!window.iHeartAnalytics && this.sdkLoaded;
  }

  private async trackEvent(event: UploadEvent, url?: string): Promise<void> {
    if (!this.isReady) {
      log.debug("SDK not ready, skipping track event");
      return;
    }

    const { action, data } = event;

    const queryPageURL = this.store.request?.query?.pageURL as string | undefined;
    const pageURL = url || queryPageURL || getPageURL();

    const payload = buildIglooPayload(event, pageURL);

    const queryRef = this.store.request?.query?.ref as string | undefined;

    const plainDeviceOb = data.device ? toJS(data.device) : {};
    const deviceData = { ...plainDeviceOb, host: getDeviceHost(), ...(queryRef && { referer: queryRef }) };
    const userData = data.user ? toJS(data.user) : {};

    window.iHeartAnalytics.setGlobalData({
      device: deviceData,
      user: userData,
    });

    log.debug(`Global device:`, deviceData);
    log.debug(`Global user:`, userData);

    window.iHeartAnalytics.track({
      type: action,
      data: payload,
    });

    log.debug(`Tracked action:`, action);
    log.debug(`Tracked data:`, payload);
  }

  loadLibrary(): Promise<void> {
    if (this._library) {
      return this._library;
    }

    this._library = new Promise<void>((resolve, reject) => {
      try {
        if (!isWindowDefined()) {
          reject(new Error("Window is not defined"));
          return;
        }

        if (window.iHeartAnalytics) {
          this.sdkLoaded = true;
          resolve();
          return;
        }

        const script = document.createElement("script");
        script.id = "iheart-analytics";
        script.src = "https://www.iheart.com/analytics/sdk/iheart-analytics.umd.js";

        script.onload = () => {
          this.sdkLoaded = true;
          log.debug("iHeart Analytics SDK loaded successfully");
          resolve();
        };

        script.onerror = () => {
          log.error("Failed to load iHeart Analytics SDK");
          reject(new Error("Failed to load iHeart Analytics SDK"));
        };

        document.head.appendChild(script);
      } catch (error) {
        reject(error);
      }
    });

    return this._library;
  }

  private trackClickAction(event: AnalyticsEvent): void {
    this.loadLibrary()
      .then(() => {
        const format = this.store.site.index.facets.find(f => f.includes("formats/"));
        const { context, pageName, sectionName, action } = event;
        const { analytics } = this.store.tags;

        const shareAsset = { ...analytics?.view.asset };
        const trackClickEvent: UploadEvent = {
          pageName,
          sectionName,
          action,
          data: {
            ...analytics,
            device: getDeviceData(analytics?.device, getDeviceId()),
            view: {
              ...analytics?.view,
              stationCallLetter: this.store.site.index.slug,
              stationFormat: format?.replace("formats/", "") || null,
              stationMarket: this.store.site.index.market?.replace("markets/", "") || null,
            },
            event: {
              location: `${pageName}|${sectionName}|${context}`.toLowerCase(),
            },
            station: {
              asset: {
                ...shareAsset,
              },
            },
            item: {
              asset: {
                ...shareAsset,
              },
            },
            window: {
              location: isWindowDefined() ? window.location.href : "",
            },
          },
        };

        this.trackEvent(trackClickEvent);
      })
      .catch((e: Error) => log.debug(e.message));
  }

  private trackContestAction(event: ContestEvent): void {
    this.loadLibrary()
      .then(() => {
        const format = this.store.site.index.facets.find(f => f.includes("formats/"));
        const { pageName, sectionName, action, id, name, exitType } = event;

        const data: {
          view: {
            asset: {
              id: string;
              name: string;
            };
            stationCallLetter: string;
            stationFormat: string | null;
            stationMarket: string | null;
          };
          contest?: {
            exitType: ContestExitType;
          };
        } = {
          view: {
            asset: {
              id: `contest|${id}`,
              name,
            },
            stationCallLetter: this.store.site.index.slug,
            stationFormat: format?.replace("formats/", "") || null,
            stationMarket: this.store.site.index.market?.replace("markets/", "") || null,
          },
        };

        if (exitType) {
          data.contest = {
            exitType,
          };
        }

        const trackContestEvent: UploadEvent = {
          pageName,
          sectionName,
          action,
          context: event.context,
          data,
        };

        this.trackEvent(trackContestEvent);
      })
      .catch((e: Error) => log.debug(e.message));
  }

  private trackShareAction(event: ShareEvent): void {
    this.loadLibrary()
      .then(() => {
        const { pageName, sectionName, action, url, share } = event;
        if (this.store.tags.analytics) {
          const { view, device } = this.store.tags.analytics;
          const shareData = {
            contentId: view.contentId,
            asset: { ...view.asset },
            authorId: view.authorId,
            pubDate: view.pubDate,
            contentOrigin: view.contentOrigin,
            contentOriginType: view.contentOriginType,
          };

          this.trackEvent(
            {
              pageName,
              sectionName,
              action,
              context: event.context,
              url: "",
              data: {
                share: { ...share },
                view: { ...shareData },
                device: getDeviceData(device, getDeviceId()),
                station: {
                  asset: {
                    id: shareData.asset?.id || "",
                    name: shareData.asset?.name || "",
                  },
                },
              },
            },
            url,
          );
        }
      })
      .catch((e: Error) => log.debug(e.message));
  }

  trackPageView(): void {
    this.loadLibrary()
      .then(() => this.collectData())
      .catch((e: Error) => log.debug(e.message));
  }

  collectData() {
    const { analytics } = this.store.tags;

    if (!analytics) {
      return;
    }

    const { session } = this.store;
    const deviceData = getDeviceData(analytics.device, getDeviceId());
    let userData: {
      profileId?: string;
      registration?: {
        birthYear?: number | null;
        gender?: string;
        type?: string;
        zip?: string;
      };
      privacyOptOut?: boolean;
      abTestGroup?: string[] | null;
    } = {
      privacyOptOut: this.ccpaStatus === CcpaStatus.OptOut,
    };

    if (session?.currentSession?.state) {
      const { testGroups } = this.store;
      if (isIHRSession(session.currentSession.state) && session.currentSession.state.authenticated) {
        const { profile } = session.currentSession.state;
        if (profile) {
          const registration =
            this.ccpaStatus === CcpaStatus.OptOut
              ? {
                  type: profile.accountType || undefined,
                }
              : {
                  type: profile.accountType || undefined,
                  gender: profile.gender || undefined,
                  birthYear: profile.birthYear || null,
                  zip: profile.zipCode || undefined,
                };

          userData = {
            ...userData,
            profileId: profile.profileId ? profile.profileId.toString() : undefined,
            registration,
          };
        }
      }

      if (testGroups) {
        userData = {
          ...userData,
          abTestGroup: Object.entries({ ...testGroups?.groups }).map(([k, v]) => `${k}|${v}`),
        };
      }
    }

    const trackPageViewEvent: UploadEvent = {
      pageName: analytics?.pageName || "unknown",
      sectionName: "",
      action: "page_view",
      context: "page_view",
      data: {
        ...analytics,
        user: userData,
        device: deviceData,
      },
    };

    this.trackEvent(trackPageViewEvent);
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  trigger = () => {};
}
